import Vue from "vue";
import Vuex from "vuex";
import { VueOfflineStorage } from "vue-offline";
import { updateField, getField } from "vuex-map-fields";
import debounce from "lodash.debounce";

Vue.use(Vuex);

const updateRemoteEbookStatesDebounced = debounce(async function () {
    // Load ebookStates from localStorage
    const ebookStates = JSON.parse(localStorage.getItem("ebookStates") || "[]");

    // Update the API with the latest ebookStates
    const saveResponse = await fetch(
        process.env.VUE_APP_API_URL + "ebooks/update-book-state",
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(ebookStates),
        }
    )
        .then((response) => response.json())
        .catch((e) => {
            console.log(e);
            return false;
        });

    if (!saveResponse) {
        return false;
    }

    const jsonResponse = saveResponse;

    if (jsonResponse.success) {
        localStorage.setItem("ebookStates", JSON.stringify(jsonResponse.data));
    }
}, 1000);

export default new Vuex.Store({
    state: {
        ebooks: [],
        ebookStates: [],
        currentHighlights: [],
        currentBookmarks: [],
        currentPageObj: {},
        currentPage: 0,
        currentBook: null,
        isSidebarActive: false,
        foundBook: false,
        foundBookmark: false,
        notLoggedIn: false,
        autoLoadedBook: false,
        activeBookmark: false,
        loading: false,
        hasLoaded: false,
        toc: [],
        rendition: null,
        currentTab: 0,
        isOnline: false,
    },
    getters: {
        getField,
        chapterNumber: (state) => {
            // Check if the book's TOC has a Table of Contents
            const hasToc = state.toc.find((toc) => toc.label.toLowerCase().includes("table of contents"));

            // Get the current chapter number, but if there is a Table of Contents, subtract 1
            return hasToc ? state.currentPageObj.chapter - 1 : state.currentPageObj.chapter;
        },
    },
    mutations: {
        updateField,
        setHasLoaded(state, payload) {
            state.hasLoaded = payload;
        },
        closeBook(state) {
            state.currentBook = null;
        },
        setSidebar(state) {
            state.isSidebarActive = !state.isSidebarActive;
        },
        setEbooks(state, payload) {
            if (Array.isArray(payload)) {
                Vue.set(state, "ebooks", payload);
            }

            const ebookStates = payload
                .filter((p) => p.product.ebook_state)
                .map((p) => p.product.ebook_state);

            if (ebookStates.length) {
                // Update localStorage with the latest ebookStates
                localStorage.setItem(
                    "ebookStates",
                    JSON.stringify(ebookStates)
                );
            }

            // TODO: Determine if this is needed
            state.ebookStates = JSON.parse(
                localStorage.getItem("ebookStates") || "[]"
            );
        },
        preloadBooks(state) {
            Object.values(state.ebooks).forEach((productObj) => {
                if (productObj) {
                    const img = new Image();
                    img.src = `${process.env.VUE_APP_API_URL}ebooks/ebooks/${productObj.product.ebookfile}.epub`;
                }
            });
        },
        autoLoadBook(state) {
            if (location.hash) {
                const ebookId = location.hash.replace("#", "");
                const foundEbook = Object.values(state.ebooks).find(
                    (productObj) => {
                        return (
                            productObj && productObj.product.ebookid === ebookId
                        );
                    }
                );

                if (foundEbook) {
                    state.autoLoadedBook = true;
                    state.currentBook = foundEbook.product.ebookfile;
                }
            }
        },
        setBookState(state, eBookId) {
            if (eBookId) {
                // Load ebookStates from localStorage
                state.ebookStates = JSON.parse(
                    localStorage.getItem("ebookStates") || "[]"
                );

                const { ebookStates } = state;
                state.currentBookmarks = [];
                state.currentHighlights = [];
                state.currentBook = eBookId;

                const ebook = ebookStates.find((book) => book.id === eBookId);

                if (ebook) {
                    state.currentHighlights = ebook.highlights;
                    state.currentBookmarks = ebook.bookmark;
                    state.currentPage = ebook.mostRecentPage;
                } else {
                    const ebookObj = {
                        id: eBookId,
                        highlights: [],
                        bookmark: [],
                        mostRecentPage: null,
                    };
                    ebookStates.push(ebookObj);
                    localStorage.setItem(
                        "ebookStates",
                        JSON.stringify(ebookStates)
                    );
                }
            }
        },
        setBookmarkState(state, bookmarkPage) {
            const { currentBook, currentBookmarks, ebookStates } = state;
            const bookmarkIndex = currentBookmarks.findIndex(
                (bookmark) =>
                    bookmark.pagePath === bookmarkPage.pagePath ||
                    (bookmark.chapter === bookmarkPage.chapter &&
                        bookmark.chapterPage === bookmarkPage.chapterPage)
            );

            if (bookmarkIndex >= 0) {
                currentBookmarks.splice(bookmarkIndex, 1);
                state.activeBookmark = false;
            } else {
                currentBookmarks.push(bookmarkPage);
                state.activeBookmark = true;
            }

            const ebook = ebookStates.find((book) => book.id === currentBook);

            if (ebook) {
                ebook.bookmark = currentBookmarks;
            }

            localStorage.setItem("ebookStates", JSON.stringify(ebookStates));
        },
        setCurrentPageState(state, currentPage) {
            const { currentBook, currentBookmarks, ebookStates } = state;

            state.currentPageObj = currentPage;
            state.currentPage = currentPage.pagePath;

            const ebook = ebookStates.find((book) => book.id === currentBook);

            if (ebook) {
                ebook.mostRecentPage = state.currentPage;
            }

            state.activeBookmark = currentBookmarks.some(
                (bookmark) =>
                    bookmark.pagePath === currentPage.pagePath ||
                    (bookmark.chapter === currentPage.chapter &&
                        bookmark.chapterPage === currentPage.chapterPage)
            );

            localStorage.setItem("ebookStates", JSON.stringify(ebookStates));
        },
        setHighlightState(state, highlightObj) {
            const { currentHighlights, currentBook, ebookStates } = state;
            const index = currentHighlights.findIndex(
                (h) => h.id === highlightObj.id
            );

            if (index >= 0) {
                if (highlightObj.canDelete) {
                    currentHighlights.splice(index, 1);
                }
            } else if (!highlightObj.canDelete) {
                currentHighlights.push(highlightObj);
            }

            const ebook = ebookStates.find((book) => book.id === currentBook);

            if (ebook) {
                ebook.highlights = currentHighlights;
            }

            localStorage.setItem("ebookStates", JSON.stringify(ebookStates));
        },
        setNotLoggedIn(state, payload) {
            state.notLoggedIn = payload;
        },
    },
    actions: {
        setBookmarkState({ commit, dispatch }, payload) {
            commit("setBookmarkState", payload);
            dispatch("updateRemoteEbookStates");
        },
        setHighlightState({ commit, dispatch }, payload) {
            commit("setHighlightState", payload);
            dispatch("updateRemoteEbookStates");
        },
        setCurrentPageState({ commit, dispatch }, payload) {
            commit("setCurrentPageState", payload);
            dispatch("updateRemoteEbookStates");
        },
        async updateRemoteEbookStates({ state }) {
            if (!state.isOnline) {
                console.log(`Skipping remote update since offline...`);
                return;
            }

            updateRemoteEbookStatesDebounced();
        },

        async getBooks({ commit }) {
            try {
                const statusResponse = await fetch(
                    process.env.VUE_APP_API_URL + "estore/status"
                );
                const statusData = await statusResponse.json();

                if (statusData.success) {
                    try {
                        const ebooksResponse = await fetch(
                            process.env.VUE_APP_API_URL + "estore/my-ebooks"
                        );
                        const ebooksData = await ebooksResponse.json();

                        if (ebooksResponse.ok && ebooksData.success) {
                            commit("setNotLoggedIn", false);
                            commit("setEbooks", ebooksData.products);
                            commit("preloadBooks");
                            commit("autoLoadBook");
                            VueOfflineStorage.set(
                                "localCachedBookData",
                                ebooksData.products
                            );
                        } else {
                            commit("setNotLoggedIn", true);
                            commit(
                                "setEbooks",
                                VueOfflineStorage.get("localCachedBookData")
                            );
                        }
                    } catch (error) {
                        console.log("There was an error!", error);
                        commit("setNotLoggedIn", true);
                        commit(
                            "setEbooks",
                            VueOfflineStorage.get("localCachedBookData")
                        );
                    } finally {
                        commit("setHasLoaded", true);
                    }
                } else {
                    commit(
                        "setEbooks",
                        VueOfflineStorage.get("localCachedBookData")
                    );
                    commit("setHasLoaded", true);
                }
            } catch (error) {
                commit(
                    "setEbooks",
                    VueOfflineStorage.get("localCachedBookData")
                );
                commit("setHasLoaded", true);
            }
        },
    },
    modules: {},
});
